import { defineStore } from "pinia";

import { CustomerApiError, customerAuthApi, CustomerSignup } from "../api/customerAuth";
import { User, UserRole, UserStatus } from "../api/models/user";

import { useCustomerOrdersStore } from "./customerOrders";

const LEGACY_CUSTOMER_STORAGE_KEYS = ["tirss:customer:token"];
const PENDING_LOGOUT_KEY = "tirss:customer:logout-pending";
export const CUSTOMER_SESSION_EVENT_KEY = "tirss:customer:session-event";
const CUSTOMER_SESSION_CHANNEL = "tirss:customer:session";

type CustomerSessionEvent = "login" | "clear";

interface CustomerSessionMessage {
  type: CustomerSessionEvent;
  id?: string;
}

interface RestoreRequest {
  generation: number;
  promise: Promise<void>;
}

let restoreRequest: RestoreRequest | null = null;
let sessionChannel: BroadcastChannel | null = null;
let lastSessionEventId = "";
let pendingLogout = false;

function storage() {
  if (typeof window === "undefined") return null;

  return window.localStorage;
}

function removeLegacyCustomerStorage() {
  if (typeof window === "undefined") return;

  LEGACY_CUSTOMER_STORAGE_KEYS.forEach((key) => {
    try {
      window.localStorage.removeItem(key);
      window.sessionStorage.removeItem(key);
    } catch {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
  });
}

removeLegacyCustomerStorage();

function hasPendingLogout() {
  try {
    return pendingLogout || storage()?.getItem(PENDING_LOGOUT_KEY) === "1";
  } catch {
    return false;
  }
}

function setPendingLogout() {
  pendingLogout = true;
  try {
    storage()?.setItem(PENDING_LOGOUT_KEY, "1");
  } catch {
    // A failed storage write does not change the server-side logout result.
  }
}

function clearPendingLogout() {
  pendingLogout = false;
  try {
    storage()?.removeItem(PENDING_LOGOUT_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted browsing contexts.
  }
}

function getSessionChannel() {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!sessionChannel) sessionChannel = new BroadcastChannel(CUSTOMER_SESSION_CHANNEL);

  return sessionChannel;
}

function createSessionEventId() {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);

  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
}

function publishSessionEvent(type: CustomerSessionEvent) {
  const message: CustomerSessionMessage = { type, id: createSessionEventId() };
  getSessionChannel()?.postMessage(message);
  try {
    storage()?.setItem(CUSTOMER_SESSION_EVENT_KEY, JSON.stringify(message));
  } catch {
    // BroadcastChannel remains available when localStorage is blocked.
  }
}

export function listenForCustomerSessionEvents(
  onEvent: (type: CustomerSessionEvent) => void
): () => void {
  const channel = getSessionChannel();
  const deliver = (message: CustomerSessionMessage) => {
    if (message.id && message.id === lastSessionEventId) return;
    if (message.id) lastSessionEventId = message.id;
    if (message.type === "login" || message.type === "clear") onEvent(message.type);
  };
  const onMessage = (event: MessageEvent<CustomerSessionMessage>) => {
    if (event.data) deliver(event.data);
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key !== CUSTOMER_SESSION_EVENT_KEY || !event.newValue) return;
    try {
      const message = JSON.parse(event.newValue) as CustomerSessionMessage;
      deliver(message);
    } catch {
      // Ignore malformed non-secret cross-tab signals.
    }
  };

  channel?.addEventListener("message", onMessage);
  window.addEventListener("storage", onStorage);

  return () => {
    channel?.removeEventListener("message", onMessage);
    window.removeEventListener("storage", onStorage);
  };
}

function mayEnter(user: User) {
  return (
    !user.roles.includes(UserRole.ADMINISTRATOR) &&
    user.roles.some((role) => role === UserRole.USER || role === UserRole.RESTAURANT_USER) &&
    (user.status === UserStatus.ACTIVE || user.status === UserStatus.UNCONFIRMED_SIGNUP)
  );
}

function restoreErrorMessage() {
  return "Não foi possível carregar sua conta. Verifique a conexão e tente novamente.";
}

export const useCustomerSession = defineStore("customerSession", {
  state: () => ({
    user: null as User | null,
    generation: 0,
    initialized: false,
    restoreError: "",
    restoring: false,
  }),
  getters: {
    initialRoute: (state) =>
      state.user?.status === UserStatus.UNCONFIRMED_SIGNUP
        ? "/confirmar-cadastro"
        : !state.user?.cpf || !state.user?.phoneNumber
        ? "/completar-cadastro"
        : "/inicio",
  },
  actions: {
    clear(options: { broadcast?: boolean } = {}) {
      this.generation++;
      useCustomerOrdersStore().clearForSessionChange();
      this.user = null;
      this.initialized = false;
      this.restoreError = "";
      this.restoring = false;
      if (options.broadcast !== false) publishSessionEvent("clear");
    },

    beginAuthentication() {
      this.generation++;
      useCustomerOrdersStore().clearForSessionChange();
      this.user = null;
      this.initialized = false;
      this.restoreError = "";
      this.restoring = false;
      publishSessionEvent("clear");
    },

    async accept(user: User, generation: number) {
      if (generation !== this.generation) {
        try {
          await customerAuthApi.logout();
        } catch {
          setPendingLogout();
        }

        return false;
      }
      if (!mayEnter(user)) {
        try {
          await customerAuthApi.logout();
        } catch {
          setPendingLogout();
        }
        throw new CustomerApiError("Acesso indisponível para esta conta.", 403);
      }

      clearPendingLogout();
      this.user = user;
      this.initialized = true;
      this.restoreError = "";
      this.restoring = false;
      publishSessionEvent("login");

      return true;
    },

    async login(email: string, password: string, remember: boolean) {
      this.beginAuthentication();
      const generation = this.generation;
      const response = await customerAuthApi.login(email.trim(), password, remember);
      await this.accept(response.user, generation);
    },

    async loginWithGoogle(credential: string, remember: boolean) {
      this.beginAuthentication();
      const generation = this.generation;
      let response;
      try {
        response = await customerAuthApi.google(credential, remember);
      } catch (error) {
        if (error instanceof CustomerApiError && error.status === 401)
          throw new CustomerApiError("Não foi possível entrar com o Google.", 401);
        if (error instanceof CustomerApiError && error.status === 503)
          throw new CustomerApiError("Entrar com o Google está indisponível no momento.", 503);
        throw error;
      }
      await this.accept(response.user, generation);
    },

    async signup(data: CustomerSignup) {
      this.beginAuthentication();
      const generation = this.generation;
      const response = await customerAuthApi.signup(data);
      await this.accept(response.user, generation);
    },

    async saveProfile(data: { cpf?: string; phoneNumber?: string }) {
      try {
        const response = await this.authenticated(() => customerAuthApi.completeProfile(data));
        if (this.user) {
          if (data.cpf !== undefined) this.user.cpf = response.cpf ?? data.cpf;
          if (data.phoneNumber !== undefined)
            this.user.phoneNumber = response.phoneNumber ?? data.phoneNumber;
        }
      } catch (error) {
        // O perfil já está completo: recarrega a sessão e segue.
        if (error instanceof CustomerApiError && error.code === "PROFILE_ALREADY_COMPLETE")
          return this.restore(true);
        throw error;
      }
    },

    async restore(force = false): Promise<void> {
      removeLegacyCustomerStorage();
      if (!force && this.initialized && !this.restoreError) return;
      if (restoreRequest) {
        if (restoreRequest.generation === this.generation) return restoreRequest.promise;
        await restoreRequest.promise;

        return this.restore(force);
      }

      const generation = this.generation;
      if (force) this.user = null;
      this.restoring = true;
      this.restoreError = "";
      const promise = (async () => {
        try {
          if (hasPendingLogout()) {
            try {
              await customerAuthApi.logout();
              clearPendingLogout();
            } catch {
              if (generation === this.generation) this.restoreError = restoreErrorMessage();

              return;
            }
          }

          const response = await customerAuthApi.session();
          if (generation !== this.generation) return;
          if (!mayEnter(response.user)) {
            try {
              await customerAuthApi.logout();
            } catch {
              setPendingLogout();
            }
            this.user = null;
            this.initialized = true;

            return;
          }
          this.user = response.user;
          this.initialized = true;
        } catch (error) {
          if (generation !== this.generation) return;
          this.user = null;
          this.initialized = true;
          if (error instanceof CustomerApiError && error.status === 401) {
            this.restoreError = "";
          } else {
            this.restoreError = restoreErrorMessage();
          }
        } finally {
          if (generation === this.generation) this.restoring = false;
        }
      })();
      restoreRequest = { generation, promise };
      try {
        await promise;
      } finally {
        if (restoreRequest?.promise === promise) restoreRequest = null;
      }
    },

    async authenticated<T>(operation: () => Promise<T>) {
      const generation = this.generation;
      if (!this.user) throw new CustomerApiError("Sua sessão expirou. Entre novamente.", 401);
      try {
        return await operation();
      } catch (error) {
        if (
          error instanceof CustomerApiError &&
          error.status === 401 &&
          generation === this.generation
        ) {
          this.clear();
        }
        throw error;
      }
    },

    async handleExternalEvent(type: CustomerSessionEvent) {
      this.clear({ broadcast: false });
      this.initialized = true;
      if (type === "login") await this.restore(true);
    },

    async logout() {
      setPendingLogout();
      this.clear();
      const generation = this.generation;
      try {
        await customerAuthApi.logout();
        if (generation === this.generation) {
          clearPendingLogout();
          this.initialized = true;
        }
      } catch (error) {
        if (generation === this.generation) setPendingLogout();
        if (error instanceof CustomerApiError && error.status === 401) {
          clearPendingLogout();
          this.initialized = true;

          return;
        }
        throw error;
      }
    },
  },
});
